import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, ChapterTag, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "AI Strategy & Solutions",
  "Generative AI / AI Agents",
  "Computer Vision",
  "Smart Glasses & Wearables",
  "IoT & Asset Tracking",
  "Hardware + Software Integration",
  "Business Process Automation",
  "AI Training",
  "Rapid Prototyping / Proof-of-Concept",
  "Something Else",
];

const expectations = [
  { num: "01", text: "A direct reply — no sales team, no handoffs." },
  { num: "02", text: "An honest assessment of whether the technology fits your problem." },
  { num: "03", text: "A practical next step, even if that step is 'don't build it yet'." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        interest: form.interest || null,
        message: form.message,
      });
      setSent(true);
      toast.success("Enquiry sent. I'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again or email iq.ai.solutions@gmail.com directly.");
      toast.error("Could not send your enquiry.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-neutral-600 focus:border-cyan focus:outline-none transition-colors duration-300";

  return (
    <main data-testid="contact-page" className="pt-20">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32">
        <ChapterTag num="09" label="Contact" />
        <MaskedLine className="mt-12 font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-5xl sm:text-7xl lg:text-8xl">
          Let's talk.
        </MaskedLine>

        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-12">
            <Reveal>
              <p className="text-base md:text-lg text-neutral-300 leading-relaxed max-w-md">
                Tell me about the problem you're trying to solve — whether it involves AI, connected devices, automation, or something you haven't seen anyone build yet.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">// DIRECT EMAIL</p>
                <a
                  href="mailto:iq.ai.solutions@gmail.com"
                  data-testid="contact-email-link"
                  className="group inline-flex items-center gap-2 font-mono text-sm tracking-[0.15em] text-cyan hover:text-cyan-hover transition-colors duration-300"
                >
                  IQ.AI.SOLUTIONS@GMAIL.COM
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-6">// WHAT TO EXPECT</p>
                <div className="space-y-5">
                  {expectations.map((e) => (
                    <div key={e.num} data-testid={`expectation-${e.num}`} className="flex gap-5 items-start border-l border-cyan/40 pl-5">
                      <span className="font-mono text-xs text-cyan mt-0.5">{e.num}</span>
                      <p className="text-sm text-neutral-400 leading-relaxed">{e.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    data-testid="contact-success-panel"
                    className="border border-cyan/40 bg-[#0A0A0A] p-10 md:p-14"
                  >
                    <CheckCircle2 size={36} className="text-cyan" strokeWidth={1.5} />
                    <h2 className="mt-8 font-unbounded font-semibold uppercase tracking-tight text-2xl md:text-3xl text-white">
                      Message received.
                    </h2>
                    <p className="mt-5 text-sm md:text-base text-neutral-400 leading-relaxed max-w-md">
                      Your enquiry has landed directly in my inbox. Expect a reply shortly — usually within one business day.
                    </p>
                    <button
                      onClick={() => { setSent(false); setForm({ name: "", email: "", company: "", interest: "", message: "" }); }}
                      data-testid="contact-send-another"
                      className="mt-10 border border-white/25 px-6 py-3 font-mono text-[11px] tracking-[0.25em] text-white hover:border-cyan hover:text-cyan transition-colors duration-300"
                    >
                      SEND ANOTHER MESSAGE
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border border-white/10 bg-[#0A0A0A] p-8 md:p-12 space-y-7"
                    data-testid="contact-form"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                      <div>
                        <label htmlFor="contact-name" className="block font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">NAME *</label>
                        <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">EMAIL *</label>
                        <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                      <div>
                        <label htmlFor="contact-company" className="block font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">COMPANY</label>
                        <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company or organization" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="contact-interest" className="block font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">AREA OF INTEREST</label>
                        <select id="contact-interest" data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className={`${inputCls} appearance-none`}>
                          <option value="">Select an area…</option>
                          {interests.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block font-mono text-[10px] tracking-[0.3em] text-neutral-500 mb-3">THE PROBLEM YOU'RE SOLVING *</label>
                      <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Describe the challenge, idea, or process you want to improve…" className={`${inputCls} resize-y`} />
                    </div>
                    {error && (
                      <p data-testid="contact-error-text" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>
                    )}
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="group inline-flex items-center gap-3 bg-cyan text-black px-10 py-4 font-mono text-xs tracking-[0.25em] font-medium hover:bg-cyan-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      {sending ? (
                        <>TRANSMITTING <Loader2 size={15} className="animate-spin" /></>
                      ) : (
                        <>SEND MESSAGE <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
